import { ReactNode, useEffect, useState } from 'react';

import { AnalyticsIcon } from '../../../assets/svg/navigation/analytics';
import { ConciliationIcon } from '../../../assets/svg/navigation/conciliation';
import { DDIcon } from '../../../assets/svg/navigation/dd';
import { International } from '../../../assets/svg/navigation/international';
import { OutboundIcon } from '../../../assets/svg/navigation/outbound';
import { PLIcon } from '../../../assets/svg/navigation/pl';
import { QTIcon } from '../../../assets/svg/navigation/qt';
import { Select } from '../../../components/form/select';
import { Input } from '../../../components/input';
import { useAuth } from '../../../hooks/useAuth';
import { useFloatingAlert } from '../../../hooks/useFloatingAlert';
import { CommerceData, MerchantCreate, Products, ProviderConfig } from '../../../models/commerce';
import { CommerceServices } from '../../../services/commerce';
import RefundResume from './components/refund-resume';
import { Switch } from './components/switch';
import {
	Button,
	ContainerButton,
	ContainerButtons,
	ContainerForm,
	ContainerSwitch,
	InputValidation,
	ModalContent,
	ModalStep,
	ModalTitle,
	QuestLabel,
	TextError,
} from './styles';

export interface Options {
	value: string | number;
	label: string | ReactNode;
	customLabel?: string;
	show: boolean;
}

interface ModalMerchantStatus {
	setShowFinalizedModal: () => void;
	onRefeth: () => void;
}
interface InfoMerchant {
	name: string;
	codeMerchant: string;
	apiToken: string;
	emailUser: string;
	father: boolean;
	idEnterprise: number;
	nameEnterprise: string | ReactNode;
}

const generateApiToken = () => {
	return [...Array(64)].map(() => Math.random().toString(36)[2]).join('');
};

const isValidName = (value: string) => /^[a-zA-Z0-9-_ ]*$/.test(value);

const ModalMerchantCreate = ({ setShowFinalizedModal, onRefeth }: ModalMerchantStatus) => {
	const { user } = useAuth();
	const { handleShowAlert } = useFloatingAlert();
	const [enterpriseList, setEnterpriseList] = useState<CommerceData[]>();
	const [optionsEnterprise, setOptionsEnterprise] = useState<Options[]>([]);
	const [step, setStep] = useState({ num: 1, title: 'Completa la información del comercio' });
	const [infoMerchant, setInfoMerchant] = useState<InfoMerchant>({
		name: '',
		codeMerchant: 'mx_',
		apiToken: '',
		emailUser: user?.email || '',
		father: false,
		idEnterprise: 0,
		nameEnterprise: '',
	});

	const provider: Options[] = [
		{ value: 'tesored', label: 'Tesored', customLabel: 'Tesored', show: true },
		{ value: 'stp', label: 'STP', customLabel: 'STP', show: true },
	];
	const [selectedOption, setSelectedOption] = useState<Options>({
		value: 'tesored',
		label: 'Tesored',
		customLabel: 'Tesored',
		show: true,
	});
	const [tesoredInfo, setTesoredInfo] = useState({
		prefix: '',
		accountId: '',
		serviceId: '',
		mainAccountNumber: '',
		dynamicAccountNumber: '',
		concept: false,
		refund: false,
	});
	const [stpInfo, setStpInfo] = useState({
		merchantName: '',
		account: '',
		concept: false,
		refund: false,
	});

	const [validationsInputs, setValidationsInputs] = useState({
		nameMerchant: { show: false, valid: false },
		urlMerchant: {
			cancel: { show: false, valid: false },
			success: { show: false, valid: false },
			webhook: { show: false, valid: false },
			general: { show: false, valid: false },
		},
		tesoredValidate: {
			prefix: { show: false, valid: false },
			accountId: { show: false, valid: false },
			serviceId: { show: false, valid: false },
			mainAccountNumber: { show: false, valid: false },
			dynamicAccountNumber: { show: false, valid: false },
		},
		stpValidate: {
			account: { show: false, valid: false },
			merchantName: { show: false, valid: false },
		},
	});

	useEffect(() => {
		setInfoMerchant((prev) => ({
			...prev,
			apiToken: generateApiToken(),
			emailUser: user?.email || '',
		}));
	}, [user?.email]);

	const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		if (value.length > 30) {
			return;
		}
		if (isValidName(value)) {
			setInfoMerchant((prev) => ({
				...prev,
				name: value,
				codeMerchant: `mx_${value.replace(/\s+/g, '_').toLowerCase()}`,
			}));
			if (value && value.length >= 3) {
				setValidationsInputs({ ...validationsInputs, nameMerchant: { show: false, valid: true } });
			} else if (!value || value.length == 0) {
				setValidationsInputs({ ...validationsInputs, nameMerchant: { show: false, valid: false } });
			} else {
				setValidationsInputs({ ...validationsInputs, nameMerchant: { show: true, valid: false } });
			}
		}
	};

	// Step 2
	const [urlMerchant, setUrlMerchant] = useState({
		cancel: '',
		success: '',
		webhook: '',
		general: '',
	});

	const handleUrlChange = (key: string, value: string) => {
		const urlRegex =
			/^http[s]?:\/\/(([a-zA-Z0-9.\-_])+(\.{1,3})*(\.[a-zA-Z0-9]{1,4})|localhost)(:[0-9]+)?\/?(\/[.\S]*)*$/;
		const sanitizedValue = value.replace(/\s+/g, '');

		setUrlMerchant((prevState) => ({
			...prevState,
			[key]: sanitizedValue,
		}));

		setValidationsInputs((prevState) => ({
			...prevState,
			urlMerchant: {
				...prevState.urlMerchant,
				[key]: { show: !urlRegex.test(sanitizedValue), valid: urlRegex.test(sanitizedValue) },
			},
		}));
	};

	// Step 3
	const handleSelectChange = (option: Options) => {
		setSelectedOption(option);
	};

	const handleTesoredChange = (key: string, value: string, min?: number, max?: number) => {
		if (max && value.length > max) {
			return;
		}
		if (min && value.length >= min) {
			setValidationsInputs({
				...validationsInputs,
				tesoredValidate: {
					...validationsInputs.tesoredValidate,
					[key]: { show: false, valid: true },
				},
			});
		} else if (!value || value.length == 0) {
			setValidationsInputs({
				...validationsInputs,
				tesoredValidate: {
					...validationsInputs.tesoredValidate,
					[key]: { show: false, valid: false },
				},
			});
		} else {
			setValidationsInputs({
				...validationsInputs,
				tesoredValidate: {
					...validationsInputs.tesoredValidate,
					[key]: { show: true, valid: false },
				},
			});
		}

		if (key === 'prefix') {
			const prefixRegex = /^[a-zA-Z0-9_]*$/;
			if (!prefixRegex.test(value)) {
				return;
			}
		}

		if (
			key === 'accountId' ||
			key === 'serviceId' ||
			key == 'dynamicAccountNumber' ||
			key == 'mainAccountNumber'
		) {
			const prefixRegex = /^[0-9]*$/;
			if (!prefixRegex.test(value)) {
				return;
			}
		}

		setTesoredInfo((prevState) => ({
			...prevState,
			[key]: value,
		}));

		if (key === 'dynamicAccountNumber' || key === 'mainAccountNumber') {
			const isValid = calcControlDigit(value);

			setValidationsInputs({
				...validationsInputs,
				tesoredValidate: {
					...validationsInputs.tesoredValidate,
					[key]: { show: !isValid, valid: isValid },
				},
			});
		}
	};

	const handleStpChange = (key: string, value: string, min?: number, max?: number) => {
		if (max && value.length > max) {
			return;
		}
		if (min && value.length >= min) {
			setValidationsInputs({
				...validationsInputs,
				stpValidate: {
					...validationsInputs.stpValidate,
					[key]: { show: false, valid: true },
				},
			});
		} else if (!value || value.length == 0) {
			setValidationsInputs({
				...validationsInputs,
				stpValidate: {
					...validationsInputs.stpValidate,
					[key]: { show: false, valid: false },
				},
			});
		} else {
			setValidationsInputs({
				...validationsInputs,
				stpValidate: {
					...validationsInputs.stpValidate,
					[key]: { show: true, valid: false },
				},
			});
		}
		if (key === 'merchantName') {
			const prefixRegex = /^[a-zA-Z0-9_]*$/;
			if (!prefixRegex.test(value)) {
				return;
			}
		}
		if (key == 'account') {
			const prefixRegex = /^[0-9]*$/;
			if (!prefixRegex.test(value)) {
				return;
			}
		}
		setStpInfo((prevState) => ({
			...prevState,
			[key]: value,
		}));
		if (key === 'account') {
			const isValid = calcControlDigit(value);

			setValidationsInputs({
				...validationsInputs,
				stpValidate: {
					...validationsInputs.stpValidate,
					[key]: { show: !isValid, valid: isValid },
				},
			});
		}
	};

	const calcControlDigit = (clabe: string) => {
		if (clabe.length !== 18) return false;
		const clabeWeights = [3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7];
		const clabeList = clabe.split('');
		const clabeInt = clabeList.map((i: string) => Number(i));
		const weighted = [];

		for (let i = 0; i < 18 - 1; i++) {
			weighted.push((clabeInt[i] * clabeWeights[i]) % 10);
		}

		const summed = weighted.reduce((curr, next) => curr + next) % 10;
		const controlDigit = (10 - summed) % 10;

		return controlDigit === clabeInt[17];
	};

	// Step 4
	const [products, setProducts] = useState([
		{
			nameProduct: 'Quick Transfer',
			fix: 'qt',
			active: false,
			icon: <QTIcon active color={'#FF7576'} />,
		},
		{
			nameProduct: 'Payment Links',
			fix: 'plm',
			active: false,
			icon: <PLIcon active color={'#857BF9'} />,
		},
		{
			nameProduct: 'Domiciliación',
			fix: 'dd_mx',
			active: false,
			icon: <DDIcon active color={'#426DD8'} />,
		},
		{
			nameProduct: 'Salidas y Devoluciones',
			fix: 'payouts',
			active: false,
			icon: <OutboundIcon active color={'#684103'} />,
		},
		{
			nameProduct: 'CrossBorder',
			fix: 'cross_border',
			active: false,
			icon: <International active color={'#252252'} />,
		},
		{
			nameProduct: 'Conciliación',
			fix: 'conciliation',
			active: false,
			icon: <ConciliationIcon active color={'#482524'} />,
		},
		{
			nameProduct: 'Analytics',
			fix: 'analytics',
			active: false,
			icon: <AnalyticsIcon active color={'#252252'} />,
		},
	]);

	const handleToggle = (index: number, isActive: boolean) => {
		setProducts((prevProducts) =>
			prevProducts.map((product, i) => (i === index ? { ...product, active: isActive } : product)),
		);
	};

	// Step 5
	const [resume, setResume] = useState([{ item: '', data: '', copy: false, value: '' }]);

	useEffect(() => {
		if (step.num == 5) {
			setResume([
				{
					item: 'Nombre',
					data: infoMerchant.name,
					copy: false,
					value: infoMerchant.name,
				},
				{
					item: 'Merchant code',
					data: infoMerchant.codeMerchant,
					copy: true,
					value: infoMerchant.codeMerchant,
				},
				{
					item: 'Merchant token',
					data: infoMerchant.apiToken.substring(0, 10) + '...',
					copy: true,
					value: infoMerchant.apiToken,
				},
				{
					item: 'Merchant padre',
					data: infoMerchant.father
						? infoMerchant.nameEnterprise
							? infoMerchant.nameEnterprise?.toString()
							: ''
						: '',
					copy: false,
					value: infoMerchant.father
						? infoMerchant.nameEnterprise
							? infoMerchant.nameEnterprise?.toString()
							: ''
						: '',
				},
				{
					item: 'Proveedor SPEI',
					data: selectedOption.value == 'stp' ? 'STP' : 'Tesored',
					copy: false,
					value: selectedOption.value == 'stp' ? 'STP' : 'Tesored',
				},
				{
					item: 'Número de cuenta',
					data: selectedOption.value == 'stp' ? stpInfo.account : tesoredInfo.mainAccountNumber,
					copy: false,
					value: selectedOption.value == 'stp' ? stpInfo.account : tesoredInfo.mainAccountNumber,
				},
				{
					item: 'Conciliación',
					data: stpInfo.concept ? 'Por CONCEPTO' : 'Por CLABE',
					copy: false,
					value: stpInfo.concept ? 'Por CONCEPTO' : 'Por CLABE',
				},
			]);
		}
	}, [step]);

	//Submit
	const generateMerchantInfo = (): MerchantCreate => {
		const merchant: Partial<MerchantCreate> = {
			name: infoMerchant.name,
			code: infoMerchant.codeMerchant,
			api_token: infoMerchant.apiToken,
			auto_refund: stpInfo.refund,
			merchant_id: '111',
			funnel_version: '1',
			payment_cancellation_url: validationsInputs.urlMerchant.cancel
				? urlMerchant.cancel
				: undefined,
			payment_redirect_url: validationsInputs.urlMerchant.general ? urlMerchant.general : undefined,
			payment_completed_url: validationsInputs.urlMerchant.success
				? urlMerchant.success
				: undefined,
			payment_webhook: validationsInputs.urlMerchant.webhook ? urlMerchant.webhook : undefined,
		};

		if (!merchant.name || !merchant.code || !merchant.api_token || !merchant.merchant_id) {
			throw new Error('Missing required fields for MerchantCreate');
		}

		return merchant as MerchantCreate;
	};

	const generateProductsEnd = (products: { fix?: string; active: boolean }[]): Products => {
		const defaultKeys: (keyof Products)[] = [
			'qt',
			'pl',
			'eatm',
			'conciliation',
			'analytics',
			'bt',
			'plm',
			'payouts',
			'dd_mx',
			'cross_border',
			'dd',
			'tnb',
			'plc',
		];

		const productsEnd: Products = defaultKeys.reduce((acc, key) => {
			acc[key] = false;
			return acc;
		}, {} as Products);

		products.forEach(({ fix, active }) => {
			if (fix && fix in productsEnd) {
				productsEnd[fix as keyof Products] = active;
			}
		});

		return productsEnd;
	};

	const generateProviderConfig = () => {
		const isSTP = selectedOption.value === 'stp';

		const providerConfig: Partial<ProviderConfig> = {
			funnel_type: stpInfo.concept ? 'concept' : 'clabe',
			name: selectedOption.value == 'stp' ? 'stp' : 'tesored',
			prefix: isSTP ? stpInfo.merchantName : tesoredInfo.prefix,
			account: isSTP ? stpInfo.account : tesoredInfo.dynamicAccountNumber,
		};

		if (!isSTP) {
			providerConfig.account_id = tesoredInfo.accountId;
			providerConfig.service_id = tesoredInfo.serviceId;
			providerConfig.main_account = tesoredInfo.mainAccountNumber;
		}

		return providerConfig as ProviderConfig;
	};
	const [buttonLoading, setButtonLoading] = useState(false);
	const [proccessFinish, setProccessFinish] = useState(false);
	const submitCreateMerchant = async () => {
		await setButtonLoading(true);
		try {
			const merchant = generateMerchantInfo();
			const productsEnd = generateProductsEnd(products);
			const providerConfig = generateProviderConfig();
			const enterpriceCreate = infoMerchant.father;
			const info = {
				enterprise: {
					id: enterpriceCreate ? infoMerchant.idEnterprise : null,
					name: infoMerchant.name,
				},
				merchant: merchant,
				products: productsEnd,
				provider_config: providerConfig,
			};

			const data = await CommerceServices.setCreateMerchant({
				operation: enterpriceCreate ? 'sub' : 'main',
				data: info,
			});
			if (data.response) {
				await setProccessFinish(true);
				await onRefeth();
				handleShowAlert({
					type: 'success',
					message: 'Comercio creado correctamente',
				});
			}
			setButtonLoading(false);
			setShowFinalizedModal();
		} catch (error: any) {
			setButtonLoading(false);
			if (error?.metadata?.code == 'MERCHANT_EXISTS') {
				return handleShowAlert({
					type: 'error',
					message: 'El comercio ya ha sido creado, intenta de nuevo',
				});
			}
			await handleShowAlert({
				type: 'error',
				message: 'Algo salió mal, intenta de nuevo más tarde',
			});
		}
	};

	//Select Merchant
	const listEnterprise = async () => {
		const data = await CommerceServices.getTableDataEnterprise({
			merchant_code: 'mx_etpay',
			page: 1,
			size: 10,
			only: 'enterprise',
		});
		if (data.tableInfo.length >= 1) {
			const enterprises = data.tableInfo
				.filter((item) => item.name !== undefined && item.id !== undefined)
				.map((item) => ({ value: item.id as number, label: item.name as string, show: true }))
				.sort((a, b) => a.label.localeCompare(b.label));

			setOptionsEnterprise(enterprises);
			setEnterpriseList(data.tableInfo);
		}
	};
	useEffect(() => {
		listEnterprise();
	}, []);

	const handlePick = (option: Options) => {
		if (enterpriseList) {
			option &&
				setInfoMerchant({
					...infoMerchant,
					idEnterprise: Number.isNaN(Number(option.value)) ? 0 : Number(option.value),
					nameEnterprise: option.label,
				});
		}
	};

	useEffect(() => {
		if (!infoMerchant.apiToken) return;
		if (infoMerchant.father) {
			handlePick(optionsEnterprise[0])
		} else {
			setInfoMerchant({
				...infoMerchant,
				idEnterprise: 0,
				nameEnterprise: '',
			}
			)
		}

	}, [infoMerchant.father])

	return (
		<ModalContent>
			<ModalStep>{step.num != 5 && `PASO ${step.num} DE 4`}</ModalStep>
			<ModalTitle>{step.title}</ModalTitle>
			{step.num == 1 && (
				<>
					<ContainerForm>
						<InputValidation>
							<Input
								label="Nombre del comercio"
								value={infoMerchant.name}
								onChange={handleNameChange}
								variant="large"
							/>
							{validationsInputs.nameMerchant.show && (
								<TextError>El texto debe tener entre 3 y 30 carácteres</TextError>
							)}
						</InputValidation>
						<Input
							label="Merchant code"
							value={infoMerchant.codeMerchant}
							disabled
							variant="large"
						/>
						<Input label="Merchant token" value={infoMerchant.apiToken} disabled variant="large" />
						<Input label="Usuario ETpay" value={infoMerchant.emailUser} disabled variant="large" />
						{enterpriseList && (
							<>
								<ContainerSwitch>
									<QuestLabel>¿Es un comercio hijo?</QuestLabel>
									<Switch
										id={'father'}
										checked={infoMerchant.father}
										onChange={(e) => setInfoMerchant({ ...infoMerchant, father: e.target.checked })}
									/>
								</ContainerSwitch>
								{infoMerchant.father && (
									<Select
										size="large"
										orientation="bottom"
										titleLabel={'Selecciona el merchant padre'}
										options={optionsEnterprise}
										onChange={handlePick}
										sizeHeight="min"
									/>
								)}
							</>
						)}
					</ContainerForm>
					<ContainerButtons>
						{enterpriseList && (
							<Button
								variant="abell"
								onClick={() => setStep({ num: 2, title: 'Configura las URLs' })}
								disabled={!validationsInputs.nameMerchant.valid}
							>
								Siguiente
							</Button>
						)}
					</ContainerButtons>
				</>
			)}
			{step.num == 2 && (
				<>
					<ContainerForm>
						<InputValidation>
							<Input
								label="URL redirección pago cancelado"
								value={urlMerchant.cancel}
								onChange={(e) => handleUrlChange('cancel', e.target.value)}
								variant="large"
							/>
							{validationsInputs.urlMerchant.cancel.show && (
								<TextError>Asegurate de ingresar una url válida</TextError>
							)}
						</InputValidation>
						<InputValidation>
							<Input
								label="URL redirección pago exitoso"
								value={urlMerchant.success}
								onChange={(e) => handleUrlChange('success', e.target.value)}
								variant="large"
							/>
							{validationsInputs.urlMerchant.success.show && (
								<TextError>Asegurate de ingresar una url válida</TextError>
							)}
						</InputValidation>
						<InputValidation>
							<Input
								label="URL de notificación webhook"
								value={urlMerchant.webhook}
								onChange={(e) => handleUrlChange('webhook', e.target.value)}
								variant="large"
							/>
							{validationsInputs.urlMerchant.webhook.show && (
								<TextError>Asegurate de ingresar una url válida</TextError>
							)}
						</InputValidation>
						<InputValidation>
							<Input
								label="URL redirección general (opcional)"
								value={urlMerchant.general}
								onChange={(e) => handleUrlChange('general', e.target.value)}
								variant="large"
							/>
							{validationsInputs.urlMerchant.general.show && (
								<TextError>Asegurate de ingresar una url válida</TextError>
							)}
						</InputValidation>
					</ContainerForm>

					<ContainerButtons>
						<ContainerButton>
							<Button
								variant="pure"
								onClick={() => setStep({ num: 1, title: 'Completa la información del comercio' })}
							>
								Regresar
							</Button>
						</ContainerButton>
						<ContainerButton>
							<Button
								variant="abell"
								onClick={() => setStep({ num: 3, title: 'Completa la configuración de SPEI' })}
								disabled={
									!(
										validationsInputs.urlMerchant.cancel.valid &&
										validationsInputs.urlMerchant.success.valid &&
										validationsInputs.urlMerchant.webhook.valid
									)
								}
							>
								Siguiente
							</Button>
						</ContainerButton>
					</ContainerButtons>
				</>
			)}
			{step.num == 3 && (
				<>
					<ContainerForm>
						<Select
							size="large"
							titleLabel={'Proveedor'}
							options={provider}
							onChange={handleSelectChange}
						/>
						<ContainerSwitch>
							<QuestLabel>Activar conciliación por concepto</QuestLabel>{' '}
							<Switch
								id={'concept'}
								checked={tesoredInfo.concept}
								onChange={(e) => {
									setTesoredInfo({ ...tesoredInfo, concept: e.target.checked }),
										setStpInfo({ ...stpInfo, concept: e.target.checked });
								}}
							/>
						</ContainerSwitch>
						<ContainerSwitch>
							<QuestLabel>Activar devoluciones automáticas</QuestLabel>{' '}
							<Switch
								id={'refund'}
								checked={tesoredInfo.refund}
								onChange={(e) => {
									setTesoredInfo({ ...tesoredInfo, refund: e.target.checked }),
										setStpInfo({ ...stpInfo, refund: e.target.checked });
								}}
							/>
						</ContainerSwitch>
						{selectedOption?.value == 'tesored' && (
							<ContainerForm>
								<InputValidation>
									<Input
										label="Prefijo"
										value={tesoredInfo.prefix}
										onChange={(e) => handleTesoredChange('prefix', e.target.value, 4, 15)}
										variant="large"
									/>
									{validationsInputs.tesoredValidate.prefix.show && (
										<TextError>El texto debe tener entre 4 y 15 carácteres</TextError>
									)}
								</InputValidation>
								<InputValidation>
									<Input
										label="ID de la cuenta"
										value={tesoredInfo.accountId}
										onChange={(e) => handleTesoredChange('accountId', e.target.value, 3, 3)}
										variant="large"
									/>
									{validationsInputs.tesoredValidate.accountId.show && (
										<TextError>El texto debe tener 3 carácteres numéricos</TextError>
									)}
								</InputValidation>
								<InputValidation>
									<Input
										label="ID del servicio"
										value={tesoredInfo.serviceId}
										onChange={(e) => handleTesoredChange('serviceId', e.target.value, 3, 3)}
										variant="large"
									/>
									{validationsInputs.tesoredValidate.serviceId.show && (
										<TextError>El texto debe tener 3 carácteres numéricos</TextError>
									)}
								</InputValidation>
								<InputValidation>
									<Input
										label="Cuenta CLABE principal"
										value={tesoredInfo.mainAccountNumber}
										onChange={(e) =>
											handleTesoredChange('mainAccountNumber', e.target.value, 18, 18)
										}
										variant="large"
									/>
									{validationsInputs.tesoredValidate.mainAccountNumber.show && (
										<TextError>
											{tesoredInfo.mainAccountNumber.length == 18
												? 'La CLABE es inválida'
												: 'El texto debe tener 18 carácteres numéricos'}
										</TextError>
									)}
								</InputValidation>
								<InputValidation>
									<Input
										label="Cuenta CLABE dinámica"
										value={tesoredInfo.dynamicAccountNumber}
										onChange={(e) =>
											handleTesoredChange('dynamicAccountNumber', e.target.value, 18, 18)
										}
										variant="large"
									/>
									{validationsInputs.tesoredValidate.dynamicAccountNumber.show && (
										<TextError>
											{tesoredInfo.dynamicAccountNumber.length == 18
												? 'La CLABE es inválida'
												: 'El texto debe tener 18 carácteres numéricos'}
										</TextError>
									)}
								</InputValidation>
							</ContainerForm>
						)}
						{selectedOption?.value == 'stp' && (
							<ContainerForm>
								<InputValidation>
									<Input
										label="Merchant name / Alias"
										value={stpInfo.merchantName}
										onChange={(e) => handleStpChange('merchantName', e.target.value, 4, 15)}
										variant="large"
									/>
									{validationsInputs.stpValidate.merchantName.show && (
										<TextError>El texto debe tener entre 4 y 15 carácteres</TextError>
									)}
								</InputValidation>
								<InputValidation>
									<Input
										label="Cuenta CLABE"
										value={stpInfo.account}
										onChange={(e) => handleStpChange('account', e.target.value)}
										variant="large"
									/>
									{validationsInputs.stpValidate.account.show && (
										<TextError>
											{stpInfo.account.length == 18
												? 'La CLABE es inválida'
												: 'El texto debe tener 18 carácteres numéricos'}
										</TextError>
									)}
								</InputValidation>
							</ContainerForm>
						)}
					</ContainerForm>

					<ContainerButtons>
						<ContainerButton>
							<Button
								variant="pure"
								onClick={() => setStep({ num: 2, title: 'Configura las URLs' })}
							>
								Regresar
							</Button>
						</ContainerButton>
						<ContainerButton>
							{selectedOption.value == 'tesored' && (
								<Button
									variant="abell"
									onClick={() =>
										setStep({ num: 4, title: 'Selecciona los productos del comercio' })
									}
									disabled={
										!(
											validationsInputs.tesoredValidate.prefix.valid &&
											validationsInputs.tesoredValidate.accountId.valid &&
											validationsInputs.tesoredValidate.serviceId.valid &&
											validationsInputs.tesoredValidate.dynamicAccountNumber.valid &&
											validationsInputs.tesoredValidate.mainAccountNumber.valid
										)
									}
								>
									Siguiente
								</Button>
							)}
							{selectedOption.value == 'stp' && (
								<Button
									variant="abell"
									onClick={() =>
										setStep({ num: 4, title: 'Selecciona los productos del comercio' })
									}
									disabled={
										!(
											validationsInputs.stpValidate.account.valid &&
											validationsInputs.stpValidate.merchantName.valid
										)
									}
								>
									Siguiente
								</Button>
							)}
						</ContainerButton>
					</ContainerButtons>
				</>
			)}
			{step.num == 4 && (
				<>
					<ContainerForm>
						{products.map((product, index) => (
							<ContainerSwitch key={index}>
								<div style={{ display: 'flex', gap: 8 }}>
									{product.icon}
									<QuestLabel>{product.nameProduct}</QuestLabel>
								</div>

								<Switch
									id={`product-${index}`}
									checked={product.active}
									onChange={(e) => handleToggle(index, e.target.checked)}
								/>
							</ContainerSwitch>
						))}
					</ContainerForm>
					<ContainerButtons>
						<ContainerButton>
							<Button
								variant="pure"
								onClick={() => setStep({ num: 3, title: 'Completa la configuración de SPEI' })}
							>
								Regresar
							</Button>
						</ContainerButton>
						<ContainerButton>
							<Button
								variant="abell"
								onClick={() => setStep({ num: 5, title: 'Detalle del comercio' })}
								disabled={!infoMerchant.name}
							>
								Siguiente
							</Button>
						</ContainerButton>
					</ContainerButtons>
				</>
			)}
			{step.num == 5 && (
				<>
					<RefundResume transferResume={resume}></RefundResume>
					{!proccessFinish && (
						<ContainerButtons>
							<ContainerButton>
								<Button
									variant="pure"
									onClick={() =>
										setStep({ num: 4, title: 'Selecciona los productos del comercio' })
									}
								>
									Regresar
								</Button>
							</ContainerButton>
							<ContainerButton>
								<Button disabled={buttonLoading} variant="abell" onClick={submitCreateMerchant}>
									Confirmar
								</Button>
							</ContainerButton>
						</ContainerButtons>
					)}
				</>
			)}
		</ModalContent>
	);
};

export { ModalMerchantCreate };
